/**
 * <plp-scroller> — prev/next arrows for a horizontally scrolling PLP track.
 * The tracks hide their scrollbar, so without these a mouse user cannot scroll
 * at all. Arrows stay hidden unless the track actually overflows.
 *
 * Markup: [data-plp-scroller-track] plus [data-plp-scroller-nav] wrapping
 * [data-plp-scroller-prev] / [data-plp-scroller-next].
 */
class PlpScroller extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.track = this.querySelector('[data-plp-scroller-track]');
    this.nav = this.querySelector('[data-plp-scroller-nav]');
    this.prev = this.querySelector('[data-plp-scroller-prev]');
    this.next = this.querySelector('[data-plp-scroller-next]');
    if (!this.track) return;

    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    window.addEventListener('on:breakpoint-change', this.update);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
    }

    this.update();
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.track.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.update);
    window.removeEventListener('on:breakpoint-change', this.update);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /** Distance of one item plus the gap: one click, one card. */
  get stepDistance() {
    const items = this.track.children;
    if (items.length < 2) return this.track.clientWidth;

    const first = items[0].getBoundingClientRect();
    const second = items[1].getBoundingClientRect();
    const span = second.left - first.left;
    return span > 0 ? span : first.width;
  }

  step(direction) {
    this.track.scrollBy({ left: this.stepDistance * direction, behavior: 'smooth' });
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const overflowing = scrollWidth - clientWidth > 1;

    this.classList.toggle('is-scrollable', overflowing);
    if (this.nav) this.nav.hidden = !overflowing;
    if (!overflowing) return;

    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }
}

customElements.define('plp-scroller', PlpScroller);
